/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import * as emotion from "emotion";
import {DB_Organization, ORGANIZATION_TYPE} from "@app/ir-q-app-common/types/care-center/organization";
import {BaseComponent, Dialog_Builder, ToastModule, TS_Checkbox, TS_Input} from "@intuitionrobotics/thunderstorm/frontend";
import {GenericSelect} from "@intuitionrobotics/thunderstorm/app-frontend/components/GenericSelect";
import {_setTimeout, DB_Object, generateHex, PartialProperties, Second} from "@intuitionrobotics/ts-common";
import {inputTextField} from "@styles/styles";
import {DialogCard} from "../../widget/DialogCard";
import {OrganizationsModule} from "@modules/OrganizationsModule";
import {OrganizationsContext, OrganizationsContextType} from "./OrganizationsContext";

type State = {
    loading: boolean
    organization: PartialProperties<DB_Organization, keyof DB_Object>
    mainContactName: string      // Local state, not part of organization
    secondContactName: string    // Local state, not part of organization
}
type Props = {
    existingOrganization?: DB_Organization;
}

const content = emotion.css(
    {
        height: 300,
        boxSizing: "border-box"
    }
);

const body = emotion.css(
    {
        padding: 20,
        width: "100%",
        boxSizing: "border-box"
    }
);

const inputText = emotion.css(
    {
        marginBottom: 15
    }
);

const inputTextLabel = emotion.css(
    {
        marginBottom: 5
    }
);

export class AddOrganizationDialog
    extends BaseComponent<Props, State> {

    public static show(existingOrganization?: DB_Organization) {
        new Dialog_Builder(
            <AddOrganizationDialog
                existingOrganization={existingOrganization}
            />
        ).setAllowIndirectClosing(true)
            .show();
    }

    constructor(props: Props) {
        super(props);

        const existingOrganization = this.props.existingOrganization;
        let key: string = "";
        if (!this.props.existingOrganization?.key)
            key = this.props.existingOrganization?.name.toLowerCase().replace(/ /g, "_") || "";

        this.state = {
            loading: false,
            organization: {
                name: "",
                type: ORGANIZATION_TYPE.INTERNAL,
                groupContactIds: [],
                key,
                ...existingOrganization
            },
            mainContactName: "",
            secondContactName: ""
        };
    }

    private submit = ({saveOrganizations}: OrganizationsContextType) => {
        const name = this.state.organization.name.trim();

        if (!name) {
            this.setState({loading: false});
            return;
        }

        // Validate MULTI_CONTACT requirements (only for new orgs)
        if (!this.props.existingOrganization &&
            this.state.organization.type === ORGANIZATION_TYPE.MULTI_CONTACT) {
            const mainName = this.state.mainContactName.trim();
            const secondName = this.state.secondContactName.trim();

            if (!mainName || !secondName) {
                ToastModule.toastError("Main and Second contact names are required for Multi-Contact organizations");
                this.setState({loading: false});
                return;
            }
        }

        // For new organizations, send CreateOrganizationRequest
        // For updates, send DB_Organization directly (existing behavior)
        const request = this.props.existingOrganization
            ? this.state.organization
            : {
                organization: this.state.organization,
                mainContactName: this.state.mainContactName || undefined,
                secondContactName: this.state.secondContactName || undefined
            };

        OrganizationsModule.createSync(request).then(createdOrg => {
            saveOrganizations([createdOrg]);
            ToastModule.toastSuccess("Organization updated, the page will automatically refresh...")
            _setTimeout(() => window.location.href = `${window.location.origin}/admin/customers`, 4 * Second)
        }).catch(console.error);
    };

    render() {
        return <OrganizationsContext.Consumer>
            {orgCtx => <DialogCard
                showX={true}
                loading={this.state.loading}
                content={this.renderAddOrganizationContent()}
                saveLabel={`${this.props.existingOrganization ? "Update" : "Add"} organization`}
                cancelLabel={"Cancel"}
                title={`${this.props.existingOrganization ? "Update" : "Add"} organization`}
                onSave={() => {
                    this.setState({loading: true}, () => this.submit(orgCtx));
                    // Keep dialog open.
                    return true;
                }}
                saveEnabled={!!(
                    this.state.organization.name.trim() &&
                    // If MULTI_CONTACT (and creating new), require contact names
                    (this.state.organization.type !== ORGANIZATION_TYPE.MULTI_CONTACT ||
                     this.props.existingOrganization ||
                     (this.state.mainContactName.trim() && this.state.secondContactName.trim()))
                )}
            />}
        </OrganizationsContext.Consumer>;
    }

    private renderAddOrganizationContent = () => {
        return <div className={`ll_v_l fill ${content}`}>
            {this.renderContent()}
        </div>;
    };

    private renderContent = () => {
        return <div className={`${body} ll_v_c`}>
            <div className={`ll_v_c`}>
                {this.renderNameField()}
            </div>
            <div className={`ll_v_c`}>
                {this.selectOrganizationType()}
            </div>
            {this.renderContactNameFields()}
            <div className={`ll_v_c`}>
                {this.renderHideDisenrolledCustomers()}
            </div>
        </div>;
    };

    private renderNameField = () => {
        return <div className={`${inputText} ll_v_l`}>
            <div className={inputTextLabel}>
                Name:
            </div>
            <div>
                <TS_Input<string>
                    className={inputTextField}
                    id={"organization-name"}
                    value={this.state.organization.name}
                    placeholder={"Name"}
                    onChange={(value: string) => this.setState(state => {
                        const organization = state.organization;
                        let key = organization.key;
                        if (!this.props.existingOrganization?.key)
                            key = value.toLowerCase().replace(/ /g, "_");

                        return ({
                            organization: {
                                ...organization,
                                name: value,
                                key
                            }
                        });
                    })}
                    type={"text"}
                    name={generateHex(8)}
                    spellCheck={true}/>
            </div>
        </div>;
    };

    private selectOrganizationType = () => {
        const type = this.state.organization.type;
        if (this.props.existingOrganization)
            return null;

        return <div className={`${inputText} ll_v_l`}>
            <div className={inputTextLabel}>
                Type:
            </div>
            <div style={{width: 220}}>
                <GenericSelect<string>
                    options={Object.values(ORGANIZATION_TYPE)}
                    selectedOption={type}
                    placeholder={type}
                    iconClose={<></>}
                    iconOpen={<></>}
                    onChange={option => {
                        const newType = option as ORGANIZATION_TYPE;
                        this.setState({
                            organization: {
                                ...this.state.organization,
                                type: newType
                            },
                            // Clear contact names when switching away from MULTI_CONTACT
                            mainContactName: newType === ORGANIZATION_TYPE.MULTI_CONTACT ? this.state.mainContactName : "",
                            secondContactName: newType === ORGANIZATION_TYPE.MULTI_CONTACT ? this.state.secondContactName : ""
                        });
                    }}
                    styles={{}}
                    presentation={option => option}
                />
            </div>
        </div>;
    };

    private renderContactNameFields = () => {
        // Only show for MULTI_CONTACT type and when creating (not editing)
        if (this.state.organization.type !== ORGANIZATION_TYPE.MULTI_CONTACT || this.props.existingOrganization) {
            return null;
        }

        return (
            <>
                <div className={`${inputText} ll_v_l`}>
                    <div className={inputTextLabel}>
                        Main Contact Name (Clinical):
                    </div>
                    <div>
                        <TS_Input<string>
                            className={inputTextField}
                            id={"main-contact-name"}
                            value={this.state.mainContactName}
                            placeholder={"Main Contact Name"}
                            onChange={(value: string) => this.setState({mainContactName: value})}
                            type={"text"}
                            name={generateHex(8)}
                            spellCheck={true}
                        />
                    </div>
                </div>
                <div className={`${inputText} ll_v_l`}>
                    <div className={inputTextLabel}>
                        Second Contact Name (External):
                    </div>
                    <div>
                        <TS_Input<string>
                            className={inputTextField}
                            id={"second-contact-name"}
                            value={this.state.secondContactName}
                            placeholder={"Second Contact Name"}
                            onChange={(value: string) => this.setState({secondContactName: value})}
                            type={"text"}
                            name={generateHex(8)}
                            spellCheck={true}
                        />
                    </div>
                </div>
            </>
        );
    };

    private renderHideDisenrolledCustomers = () => {
        const hideDisenrolledCustomers = !!this.state.organization.hideDisenrolledCustomers;
        return <div className={`${inputText} ll_v_l`}>
            <div>
                <TS_Checkbox
                    value={hideDisenrolledCustomers}
                    checked={hideDisenrolledCustomers}
                    label={"Hide Disenrolled Customers"}
                    onCheck={() => this.setState(state => ({
                        organization: {
                            ...state.organization,
                            hideDisenrolledCustomers: !state.organization.hideDisenrolledCustomers
                        }
                    }))}
                />
            </div>
        </div>;
    };
}
